import React, {useMemo} from 'react';
import {Tabs} from 'antd';
import MenuManagement from './MenuManagement';
import GroupManagement from './GroupManagement';
import UserManagement from './UserManagement';
import AuthorityManagement from './AuthorityManagement';
import APIManagement from './APIManagement';
import EventManagement from './EventManagement';
import FileManagement from './FileManagement';
import {useMenuContext} from '../contexts/MenuContext';

/**
 * 시스템 설정 페이지: 메뉴관리 설정(useYn/정렬)에 따라 탭을 동적으로 구성
 */
const SystemSettingsPage = () => {
  const { menus } = useMenuContext() || { menus: [] };

  // rteKey -> Component 로컬 매핑(선언적, 순환참조 방지)
  const compMap = useMemo(() => ({
    MENU_MANAGE: MenuManagement,
    GROUP_MANAGE: GroupManagement,
    USER_MANAGE: UserManagement,
    AUTHORITY_MANAGE: AuthorityManagement,
    API_MANAGE: APIManagement,
    EVENT_MANAGE: EventManagement,
    FILE_MANAGE: FileManagement,
  }), []);

  const sysTabs = useMemo(() => {
    const list = Array.isArray(menus) ? menus : [];
    // 시스템설정 루트 찾기: rteKey === 'SYSTEM_SETTINGS' 또는 path === '/system/menus'
    const sysRoot = list.find(m => m.rteKey === 'SYSTEM_SETTINGS') || list.find(m => m.path === '/system/menus');
    if (!sysRoot) return null;
    // 자식 목록 추출 -> 사용여부 필터 -> sortSeq 정렬
    const children = list
      .filter(m => m.parentMenuId === sysRoot.menuId)
      .filter(m => (m.useYn ?? 'Y') !== 'N')
      .sort((a,b)=> (a.sortSeq ?? 0) - (b.sortSeq ?? 0));
    // 탭 아이템 생성: 매핑 가능한 rteKey만 탭으로
    const items = children.map((m) => {
      const Comp = compMap[m.rteKey];
      if (!Comp) return null; // 매핑 불가 항목은 스킵
      return {
        key: m.rteKey || m.menuId,
        label: m.menuNm || m.rteKey,
        children: <Comp />,
      };
    }).filter(Boolean);
    return items && items.length ? items : null;
  }, [menus, compMap]);

  // fallback: 레지스트리 또는 메뉴가 비어있을 때 기존 고정 탭 사용
  const fallbackItems = [
    { key: 'menus', label: '메뉴 관리', children: <MenuManagement /> },
    { key: 'groups', label: '그룹 관리', children: <GroupManagement /> },
    { key: 'users', label: '사용자 관리', children: <UserManagement /> },
    { key: 'authorities', label: '권한 관리', children: <AuthorityManagement /> },
    { key: 'apis', label: 'API 관리', children: <APIManagement /> },
    { key: 'files', label: '파일 관리', children: <FileManagement /> },
    { key: 'events', label: '이벤트 관리', children: <EventManagement /> }
  ];

  const items = sysTabs || fallbackItems;

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
      <Tabs items={items} defaultActiveKey={items[0]?.key} destroyInactiveTabPane />
    </div>
  );
};

export default SystemSettingsPage;
